import { addDays, format, isSaturday, isWeekend, nextSaturday, nextSunday, nextTuesday } from 'date-fns';

export interface SchedulePreset {
  label: string;
  sublabel: string;
  value: string;
}

// Predefined slots land within +-this many minutes of their nominal time, so that users who
// all click the same preset don't all schedule for the exact same instant.
const JITTER_MINUTES = 9;

function jitterMinutes(): number {
  return Math.floor(Math.random() * (JITTER_MINUTES * 2 + 1)) - JITTER_MINUTES;
}

interface PresetSpec {
  dayLabel: string;
  date: Date;
  hours: number;
  minutes: number;
}

/**
 * Quick-pick send/reschedule times: today (if still reachable), next business day, tomorrow,
 * next Tuesday, and the weekend slots. The "Custom" date/time inputs next to these are separate
 * state and are never touched by this — they always send exactly what the user typed.
 */
export function getSchedulePresets(): SchedulePreset[] {
  const now = new Date();
  const tomorrow = addDays(now, 1);
  const nextBizDay = isWeekend(tomorrow) ? addDays(tomorrow, isSaturday(tomorrow) ? 2 : 1) : tomorrow;
  const nextTue = nextTuesday(now);
  const nextSat = nextSaturday(now);
  const nextSun = nextSunday(now);

  // A "Today" slot only appears while its nominal time, even after the worst-case negative
  // jitter, is still ahead of now.
  const reachableToday = (hours: number, minutes: number) => {
    const cutoff = hours * 60 + minutes - JITTER_MINUTES;
    return now.getHours() * 60 + now.getMinutes() < cutoff;
  };

  // The evening slot is offered once, on whichever day it comes round next: today while 20:30
  // is still ahead of now, tomorrow otherwise.
  const eveningToday = reachableToday(20, 30);

  const specs: PresetSpec[] = [
    ...(reachableToday(10, 30) ? [{ dayLabel: 'Today', date: now, hours: 10, minutes: 30 }] : []),
    ...(reachableToday(14, 30) ? [{ dayLabel: 'Today', date: now, hours: 14, minutes: 30 }] : []),
    ...(eveningToday ? [{ dayLabel: 'Today', date: now, hours: 20, minutes: 30 }] : []),
    { dayLabel: 'Next business day', date: nextBizDay, hours: 10, minutes: 30 },
    { dayLabel: 'Tomorrow', date: tomorrow, hours: 10, minutes: 30 },
    { dayLabel: 'Tomorrow', date: tomorrow, hours: 14, minutes: 30 },
    ...(eveningToday ? [] : [{ dayLabel: 'Tomorrow', date: tomorrow, hours: 20, minutes: 30 }]),
    { dayLabel: 'Next Tuesday', date: nextTue, hours: 10, minutes: 30 },
    { dayLabel: 'Next Saturday', date: nextSat, hours: 15, minutes: 30 },
    { dayLabel: 'Next Sunday', date: nextSun, hours: 16, minutes: 30 },
  ];

  // Dedup by calendar day + nominal time (before jitter), since e.g. "Next business day" and
  // "Tomorrow" are the same slot whenever tomorrow isn't a weekend.
  const seen = new Set<string>();
  const deduped = specs.filter((s) => {
    const key = `${format(s.date, 'yyyy-MM-dd')}_${s.hours}:${s.minutes}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return deduped.map((s) => {
    const slot = new Date(s.date);
    slot.setHours(s.hours, s.minutes + jitterMinutes(), 0, 0);
    return {
      label: `${s.dayLabel}, ${format(slot, 'HH:mm')}`,
      sublabel: format(s.date, 'EEE MMM d'),
      value: slot.toISOString(),
    };
  });
}
